<template>
    <v-row justify="center">
        <v-dialog v-model="show_payment_settings_dialog" max-width="800" persistent scrollable>
            <v-card>
                <v-card-title class="text-h5 pb-0">Payment Settings</v-card-title>
                <v-card-subtitle class="pt-1">Configure payment gateway and currency settings</v-card-subtitle>
                <v-card-text class="pt-4">
                    <!-- General Settings Section -->
                    <div class="section-header mb-3">General Settings</div>
                    <v-row>
                        <v-col cols="6">
                            <v-select v-model="selected_currency"
                                :items="currencies"
                                label="Default Currency"
                                outlined dense
                                return-object
                                item-text="code"
                                item-value="id">
                                <template v-slot:item="{item}">
                                    {{ item.code }}: {{ item.symbol }}
                                </template>
                                <template v-slot:selection="{ item }">
                                    {{ item.code }}: {{ item.symbol }}
                                </template>
                            </v-select>
                        </v-col>
                        <v-col cols="6">
                            <v-combobox v-model="payment_setting_data.gateway" 
                                label="Payment Gateway" 
                                outlined dense
                                :items="['Sagepay', 'OpayoPi', 'Stripe', 'Authipay']"></v-combobox>
                        </v-col>
                    </v-row>

                    <v-divider class="my-4"></v-divider>

                    <!-- Gateway Configuration Section -->
                    <div class="section-header mb-3">Gateway Configuration</div>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field dense outlined 
                                label="Gateway URL" 
                                v-model="payment_setting_data.gateway_url"
                                hint="The API endpoint URL for the payment gateway"
                                persistent-hint></v-text-field>
                        </v-col>
                        <v-col cols="12" v-if="!['Authipay', 'OpayoPi'].includes(payment_setting_data.gateway)">
                            <v-text-field dense outlined
                                label="API Key"
                                v-model="payment_setting_data.key"
                                :type="showApiKey ? 'text' : 'password'"
                                :append-icon="showApiKey ? 'mdi-eye' : 'mdi-eye-off'"
                                @click:append="showApiKey = !showApiKey"></v-text-field>
                        </v-col>
                    </v-row>

                    <!-- Stripe Settings (conditional) -->
                    <template v-if="payment_setting_data.gateway == 'Stripe'">
                        <v-divider class="my-4"></v-divider>
                        <div class="section-header mb-3">Stripe Configuration</div>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field dense outlined 
                                    label="Publishable Key" 
                                    v-model="payment_setting_data.published_key"
                                    hint="Your Stripe publishable key (starts with pk_)"
                                    persistent-hint></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field dense outlined 
                                    label="Secret Key" 
                                    v-model="payment_setting_data.secret_key"
                                    :type="showSecretKey ? 'text' : 'password'"
                                    :append-icon="showSecretKey ? 'mdi-eye' : 'mdi-eye-off'"
                                    @click:append="showSecretKey = !showSecretKey"
                                    hint="Your Stripe secret key (starts with sk_)"
                                    persistent-hint></v-text-field>
                            </v-col>
                        </v-row>
                    </template>

                    <!-- Authipay Settings (conditional) -->
                    <template v-if="payment_setting_data.gateway == 'Authipay'">
                        <v-divider class="my-4"></v-divider>
                        <div class="section-header mb-3">Authipay Configuration</div>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field dense outlined 
                                    label="Store Name/ID" 
                                    v-model="payment_setting_data.key"
                                    hint="Your Authipay Store Name or Store ID"
                                    persistent-hint></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field dense outlined 
                                    label="Shared Secret" 
                                    v-model="payment_setting_data.secret_key"
                                    :type="showSecretKey ? 'text' : 'password'"
                                    :append-icon="showSecretKey ? 'mdi-eye' : 'mdi-eye-off'"
                                    @click:append="showSecretKey = !showSecretKey"
                                    hint="Your Authipay shared secret key"
                                    persistent-hint></v-text-field>
                            </v-col>
                        </v-row>
                    </template>

                    <!-- Opayo Pi Settings (conditional) -->
                    <template v-if="payment_setting_data.gateway == 'OpayoPi'">
                        <v-divider class="my-4"></v-divider>
                        <div class="section-header mb-3">Opayo Pi Configuration</div>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field dense outlined
                                    label="Integration Key"
                                    v-model="payment_setting_data.key"
                                    hint="Gateway URL is the API base, e.g. https://live.opayo.eu.elavon.com/api/v1. Vendor name is the company's Sagepay vendor."
                                    persistent-hint></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field dense outlined
                                    label="Integration Password"
                                    v-model="payment_setting_data.secret_key"
                                    :type="showSecretKey ? 'text' : 'password'"
                                    :append-icon="showSecretKey ? 'mdi-eye' : 'mdi-eye-off'"
                                    @click:append="showSecretKey = !showSecretKey"
                                    hint="Your Opayo Pi integration password"
                                    persistent-hint></v-text-field>
                            </v-col>
                        </v-row>
                    </template>

                    <v-divider class="my-4"></v-divider>

                    <!-- Test Mode Settings Section -->
                    <div class="section-header mb-3">Test Mode Configuration</div>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field dense outlined 
                                label="Test Gateway URL" 
                                v-model="payment_setting_data.test_gateway_url"
                                hint="The test/sandbox API endpoint URL"
                                persistent-hint></v-text-field>
                        </v-col>
                        <v-col cols="12" v-if="!['Authipay', 'OpayoPi'].includes(payment_setting_data.gateway)">
                            <v-text-field dense outlined
                                label="Test API Key"
                                v-model="payment_setting_data.test_key"
                                :type="showTestApiKey ? 'text' : 'password'"
                                :append-icon="showTestApiKey ? 'mdi-eye' : 'mdi-eye-off'"
                                @click:append="showTestApiKey = !showTestApiKey"
                                hint="Test/sandbox API key"
                                persistent-hint></v-text-field>
                        </v-col>
                    </v-row>

                    <!-- Test Mode Stripe Settings (conditional) -->
                    <template v-if="payment_setting_data.gateway == 'Stripe'">
                        <v-row>
                            <v-col cols="12">
                                <v-text-field dense outlined 
                                    label="Test Publishable Key" 
                                    v-model="payment_setting_data.test_published_key"
                                    hint="Your Stripe test publishable key (starts with pk_test_)"
                                    persistent-hint></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field dense outlined 
                                    label="Test Secret Key" 
                                    v-model="payment_setting_data.test_secret_key"
                                    :type="showTestSecretKey ? 'text' : 'password'"
                                    :append-icon="showTestSecretKey ? 'mdi-eye' : 'mdi-eye-off'"
                                    @click:append="showTestSecretKey = !showTestSecretKey"
                                    hint="Your Stripe test secret key (starts with sk_test_)"
                                    persistent-hint></v-text-field>
                            </v-col>
                        </v-row>
                    </template>

                    <!-- Test Mode Authipay Settings (conditional) -->
                    <template v-if="payment_setting_data.gateway == 'Authipay'">
                        <v-row>
                            <v-col cols="12">
                                <v-text-field dense outlined 
                                    label="Test Store Name/ID" 
                                    v-model="payment_setting_data.test_key"
                                    hint="Your Authipay test Store Name or Store ID"
                                    persistent-hint></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field dense outlined 
                                    label="Test Shared Secret" 
                                    v-model="payment_setting_data.test_secret_key"
                                    :type="showTestSecretKey ? 'text' : 'password'"
                                    :append-icon="showTestSecretKey ? 'mdi-eye' : 'mdi-eye-off'"
                                    @click:append="showTestSecretKey = !showTestSecretKey"
                                    hint="Your Authipay test shared secret key"
                                    persistent-hint></v-text-field>
                            </v-col>
                        </v-row>
                    </template>

                    <!-- Test Mode Opayo Pi Settings (conditional) -->
                    <template v-if="payment_setting_data.gateway == 'OpayoPi'">
                        <v-row>
                            <v-col cols="12">
                                <v-text-field dense outlined
                                    label="Test Integration Key"
                                    v-model="payment_setting_data.test_key"
                                    hint="Sandbox gateway URL: https://sandbox.opayo.eu.elavon.com/api/v1"
                                    persistent-hint></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field dense outlined
                                    label="Test Integration Password"
                                    v-model="payment_setting_data.test_secret_key"
                                    :type="showTestSecretKey ? 'text' : 'password'"
                                    :append-icon="showTestSecretKey ? 'mdi-eye' : 'mdi-eye-off'"
                                    @click:append="showTestSecretKey = !showTestSecretKey"
                                    hint="Your Opayo Pi sandbox integration password"
                                    persistent-hint></v-text-field>
                            </v-col>
                        </v-row>
                    </template>

                    <v-divider class="my-4"></v-divider>

                    <!-- Redirect URLs Section -->
                    <div class="section-header mb-3">Redirect URLs</div>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field dense outlined 
                                label="Frontend URL" 
                                v-model="payment_setting_data.frontend_url"
                                hint="Base URL of the client application"
                                persistent-hint></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field dense outlined 
                                label="Success URL" 
                                v-model="payment_setting_data.success_url"
                                hint="Redirect URL after successful payment"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field dense outlined 
                                label="Failure URL" 
                                v-model="payment_setting_data.failure_url"
                                hint="Redirect URL after failed payment"></v-text-field>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn class="custom-buttons" text @click="closePaymentSettingDialog()">Cancel</v-btn>
                    <v-btn class="custom-buttons" color="primary" @click="savePaymentSettings()">Save Changes</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    components: {
    },
    props: [
        'show_payment_settings_dialog'
    ],
    data: () => ({
        showApiKey: false,
        showSecretKey: false,
        showTestApiKey: false,
        showTestSecretKey: false,
        selected_currency: null,
        currencies: [],
        payment_setting_data: {
            gateway: null,
            gateway_url: null,
            company_id: null,
            currency_setting_id: null,
            key: null,
            success_url: null,
            failure_url: null,
            frontend_url: null,
            published_key: null,
            secret_key: null,
            test_published_key: null,
            test_secret_key: null,
            test_gateway_url: null,
            test_key: null,
            id: null,
        }
    }),
    computed:{
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
        })
    },
    methods: {
        closePaymentSettingDialog() {
            this.$emit('closePaymentSettingsDialog')
        },
        async savePaymentSettings() {
            this.closePaymentSettingDialog()
            this.payment_setting_data.currency_setting_id = this.selected_currency.id
            const url = this.payment_setting_data.id == null
                ? 'admin/company_settings/save_payment_settings'
                : `admin/company_settings/update_payment_settings/${this.payment_setting_data.id}`
            const response = await this.$axios.post(url, this.payment_setting_data)
            const result = response.data
            if ( result.response == true ) {
                this.payment_setting_data = result.data
            }
        }
    },
    async mounted() {
        const response = await this.$axios.get('admin/company_settings/get_currencies')
        const result = response.data
        if ( result.response == true ) {
            this.currencies = [...result.data]
        }
    },
    watch: {
        show_payment_settings_dialog: async function() {
            const response = await this.$axios.get(`admin/company_settings/get_payment_settings/${this.get_selected_company.id}`)
            const result = response.data
            if ( result.response == true ) {
                if ( result.data != null ) {
                    this.payment_setting_data.id = result.data.id
                    this.payment_setting_data.gateway = result.data.gateway
                    this.payment_setting_data.gateway_url = result.data.gateway_url
                    this.payment_setting_data.key = result.data.key
                    this.payment_setting_data.published_key = result.data.published_key
                    this.payment_setting_data.secret_key = result.data.secret_key
                    this.payment_setting_data.success_url = result.data.success_url
                    this.payment_setting_data.test_published_key = result.data.test_published_key
                    this.payment_setting_data.test_secret_key = result.data.test_secret_key
                    this.payment_setting_data.test_gateway_url = result.data.test_gateway_url
                    this.payment_setting_data.test_key = result.data.test_key
                    this.payment_setting_data.failure_url = result.data.failure_url
                    this.payment_setting_data.frontend_url = result.data.frontend_url

                    // Search Default Currency
                    this.selected_currency = this.currencies.find( (item) => item.id == result.data.currency_setting_id)
                }
            }
            this.payment_setting_data.company_id = this.get_selected_company.id
        }
    }
}
</script>

<style scoped lang="scss">
.section-header {
    font-family: Manrope, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #424242;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

:deep(.custom-buttons span) {
    text-transform: none;
    font-family: Manrope;
    font-size: 13px !important;
    font-style: normal;
    font-weight: 600 !important;
    line-height: normal;
    box-shadow: none !important;
}

:deep(.custom-buttons button) {
    padding: 8px 7px 8px 7px !important;
    border-radius: 5px;
}

:deep(.v-text-field .v-messages) {
    font-size: 11px;
}

::-webkit-scrollbar {
    width: 7px;
    background: white;
}

::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background-color: #D9D9D9;
    color: #D9D9D9;
    height: 50px;
}
</style>